import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { Loader2, Rocket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface GoLiveConfirmDialogProps {
  /** The property's backend-served canonical path. The PATCH target is this + '/go-live'. */
  propertyBasePath: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The go-live confirmation, shared by BOTH entrypoints — the header badge's popover
 * (PushStatusBadge, the permanent control) and the dashboard banner (GoLiveControl, the
 * transient conversion surface). Neither one redirects to the other; they are peers that
 * open the same dialog, so the copy and the request can never drift apart.
 *
 * Callers own their own trigger and their own billing-blocked affordance (a Tooltip on the
 * banner, an inline note in the popover) — the entitlement gate itself lives server-side in
 * ToggleGoLiveMutation, so those are display hints only.
 *
 * Unlike pausing, going live needs no password: it is the intended direction and its effect
 * is loud (rates land in the PMS). See PausePushDialog for the asymmetry.
 */
export default function GoLiveConfirmDialog({ propertyBasePath, open, onOpenChange }: GoLiveConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false)

  function goLive() {
    router.patch(
      `${propertyBasePath}/go-live`,
      { live: true },
      {
        onStart: () => setSubmitting(true),
        onSuccess: () => onOpenChange(false),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Take this property live?</DialogTitle>
          <DialogDescription>
            This pushes the latest computed rates to your PMS and keeps them in sync on every
            computation. You can pause at any time from the status badge in the header.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-lg"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={goLive}
            disabled={submitting}
            className="rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Going live…
              </>
            ) : (
              <>
                <Rocket className="mr-1.5 h-3.5 w-3.5" />
                Go live
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
